
import React, { useState } from 'react';
import { LabTestRequest, LabRig, LabSample, LabRequestStatus, RigStatus, AuditLogEntry, LabDeviation, LabDailyCheck, LabUser, LabRole, ShiftType } from '../types';
import {
  CheckCircle2,
  AlertTriangle,
  Plus,
  Search,
  CheckSquare,
  XCircle,
  QrCode,
  History,
  Wrench,
  Settings,
  ShieldAlert,
  CalendarCheck,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Users,
  ToggleLeft,
  ToggleRight,
  Edit2,
  Trash2,
  List,
  Clock3,
  BookOpen
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

interface LimsModuleProps {
  requests: LabTestRequest[];
  setRequests: React.Dispatch<React.SetStateAction<LabTestRequest[]>>;
  rigs: LabRig[];
  setRigs: React.Dispatch<React.SetStateAction<LabRig[]>>;
  samples: LabSample[];
  setSamples: React.Dispatch<React.SetStateAction<LabSample[]>>;
}

// Mock Data
const MOCK_PROJECTS = [
  { code: 'PRJ-2025-A', name: 'ECU Gen 5 Validation', customer: 'Automotive OEM A' },
  { code: 'PRJ-2025-B', name: 'Inverter High Power', customer: 'Energy Systems Inc.' },
  { code: 'PRJ-2025-C', name: 'Connector Housing Durability', customer: 'Tier 1 Supplier' },
];

const INITIAL_PROTOCOLS = [
  {
    id: 'PROT-001',
    name: 'Thermal Cycling - Automotive',
    standard: 'ISO 16750-4',
    duration: 168,
    gates: ['calibrationChecked', 'samplesVerified'],
    defaultParams: [
      { key: 'tempLow', label: 'Low Temp', type: 'number', default: -40, unit: '°C' },
      { key: 'tempHigh', label: 'High Temp', type: 'number', default: 85, unit: '°C' },
      { key: 'cycles', label: 'Target Cycles', type: 'number', default: 1000, unit: 'cyc' },
      { key: 'dwell', label: 'Dwell Time', type: 'number', default: 30, unit: 'min' },
    ]
  },
  {
    id: 'PROT-002',
    name: 'Random Vibration',
    standard: 'GMW 3172',
    duration: 24,
    gates: ['calibrationChecked', 'samplesVerified', 'environmentChecked'],
    defaultParams: [
      { key: 'axis', label: 'Axis', type: 'text', default: 'Z-Axis', unit: '-' },
      { key: 'duration', label: 'Duration', type: 'number', default: 24, unit: 'hrs' },
      { key: 'profile', label: 'Profile Type', type: 'text', default: 'Rough Road', unit: '-' },
    ]
  },
  {
    id: 'PROT-003',
    name: 'High Temp Operating Life (HTOL)',
    standard: 'JEDEC JESD22',
    duration: 1000,
    gates: ['calibrationChecked', 'samplesVerified', 'environmentChecked'],
    defaultParams: [
      { key: 'temp', label: 'Temperature', type: 'number', default: 125, unit: '°C' },
      { key: 'voltage', label: 'Bias Voltage', type: 'number', default: 12.0, unit: 'V' },
      { key: 'duration', label: 'Duration', type: 'number', default: 1000, unit: 'hrs' },
    ]
  },
];

const INITIAL_USERS: LabUser[] = [
  { id: 'U-001', name: 'Alex Chen', email: 'alex.c@reliability.pro', role: 'Manager', shift: '1st Shift (Day)', status: 'Active', skills: ['Thermal', 'Admin'] },
  { id: 'U-002', name: 'Sarah Jones', email: 's.jones@reliability.pro', role: 'Technician', shift: '1st Shift (Day)', status: 'Active', skills: ['Vibration', 'Mechanical'] },
  { id: 'U-003', name: 'Mike Ross', email: 'm.ross@reliability.pro', role: 'Technician', shift: '2nd Shift (Eve)', status: 'Active', skills: ['Electrical', 'Thermal'] },
  { id: 'U-004', name: 'Jessica Pearson', email: 'j.pearson@reliability.pro', role: 'Engineer', shift: '1st Shift (Day)', status: 'On Leave', skills: ['Analysis', 'Reporting'] },
];

export const LimsModule: React.FC<LimsModuleProps> = ({
  requests, setRequests, rigs, setRigs, samples, setSamples
}) => {
  const [view, setView] = useState<'dashboard' | 'scheduler' | 'requests' | 'execution' | 'samples' | 'rigs' | 'admin'>('dashboard');
  const [selectedRequest, setSelectedRequest] = useState<LabTestRequest | null>(null);
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
  const [protocols, setProtocols] = useState(INITIAL_PROTOCOLS);
  const [users, setUsers] = useState<LabUser[]>(INITIAL_USERS);

  // --- Actions ---

  // Helper to create audit entry
  const createAuditEntry = (action: string, details: string): any => ({
    id: Date.now().toString() + Math.random().toString().slice(2, 5),
    timestamp: new Date().toISOString(),
    user: 'Alex Chen', // Mock current user
    action,
    details
  });

  /*
  const _addAuditEntry = (reqId: string, action: string, details: string) => {
    setRequests(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      return { ...req, auditLog: [createAuditEntry(action, details), ...(req.auditLog || [])] };
    }));
  };
  */

  const handleStatusChange = (id: string, newStatus: LabRequestStatus, rigId?: string) => {
    // 1. Handle Side Effects (Rig Updates)
    if (rigId) {
      setRigs(currentRigs => currentRigs.map(r =>
        r.id === rigId ? { ...r, status: RigStatus.IN_USE, currentTestId: id } : r
      ));
    } else if (newStatus === LabRequestStatus.COMPLETED) {
      // Find the request to see which rig to free
      const req = requests.find(r => r.id === id);
      if (req && req.assignedRigId) {
        setRigs(currentRigs => currentRigs.map(r =>
          r.id === req.assignedRigId ? { ...r, status: RigStatus.AVAILABLE, currentTestId: undefined } : r
        ));
      }
    }

    // 2. Update Request State & Audit Log atomically
    setRequests(prev => prev.map(req => {
      if (req.id !== id) return req;

      const updates: Partial<LabTestRequest> = { status: newStatus };
      if (rigId) updates.assignedRigId = rigId;

      let logMessage = `Status changed to ${newStatus}`;
      if (rigId) logMessage = `Assigned to rig ${rigId}`;
      if (newStatus === LabRequestStatus.COMPLETED) logMessage = 'Test execution completed';
      if (newStatus === LabRequestStatus.APPROVED) logMessage = 'Request approved';

      return {
        ...req,
        ...updates,
        auditLog: [createAuditEntry(newStatus === LabRequestStatus.IN_PROGRESS && rigId ? 'RIG_ASSIGN' : 'STATUS_CHANGE', logMessage), ...(req.auditLog || [])]
      };
    }));
  };

  const handleAutoAssign = (reqId: string) => {
    // Find first available rig
    const availableRig = rigs.find(r => r.status === RigStatus.AVAILABLE);

    if (availableRig) {
      handleStatusChange(reqId, LabRequestStatus.IN_PROGRESS, availableRig.id);
    } else {
      alert("No available rigs found. Please wait for a resource to free up.");
    }
  };

  /* 
  const handleRetest = (req: LabTestRequest) => {
    ...
  };
  */

  const handleEscalateCAPA = (reqId: string, devId: string) => {
    const capaId = `CAPA-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000)}`;
    setRequests(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      const updatedDeviations = req.deviations?.map(dev =>
        dev.id === devId ? { ...dev, capaId: capaId } : dev
      );
      return {
        ...req,
        deviations: updatedDeviations,
        auditLog: [createAuditEntry('CAPA_ESCALATION', `Deviation ${devId} escalated to ${capaId}`), ...(req.auditLog || [])]
      };
    }));
  };

  const handleQCGateToggle = (reqId: string, gate: keyof LabTestRequest['qcGates']) => {
    setRequests(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      const newVal = !req.qcGates[gate];

      return {
        ...req,
        qcGates: { ...req.qcGates, [gate]: newVal },
        auditLog: [createAuditEntry('QC_GATE', `${gate} set to ${newVal ? 'PASS' : 'FAIL'}`), ...(req.auditLog || [])]
      };
    }));
  };

  const handleRigAction = (rigId: string, action: 'maintenance' | 'calibrate') => {
    setRigs(prev => prev.map(rig => {
      if (rig.id !== rigId) return rig;
      if (action === 'maintenance') {
        const newStatus = rig.status === RigStatus.MAINTENANCE ? RigStatus.AVAILABLE : RigStatus.MAINTENANCE;
        return { ...rig, status: newStatus };
      }
      if (action === 'calibrate') {
        const nextDate = new Date();
        nextDate.setMonth(nextDate.getMonth() + 6);
        return {
          ...rig,
          status: RigStatus.AVAILABLE,
          calibrationDue: nextDate.toISOString().split('T')[0]
        };
      }
      return rig;
    }));
  };

  const handleSampleAction = (sampleId: string, action: LabSample['status']) => {
    setSamples(prev => prev.map(s => s.id === sampleId ? { ...s, status: action } : s));
  };

  const addDeviation = (reqId: string, type: LabDeviation['type'], description: string) => {
    const deviation: LabDeviation = {
      id: 'DEV-' + Date.now().toString().slice(-4),
      timestamp: new Date().toISOString(),
      type,
      description,
      loggedBy: 'Alex Chen'
    };

    setRequests(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      return {
        ...req,
        deviations: [deviation, ...(req.deviations || [])],
        auditLog: [createAuditEntry('DEVIATION', `Logged ${type} deviation: ${description}`), ...(req.auditLog || [])]
      };
    }));
  };

  const addDailyCheck = (reqId: string, status: 'Nominal' | 'Warning' | 'Error', notes: string) => {
    const check: LabDailyCheck = {
      id: 'DC-' + Date.now(),
      timestamp: new Date().toISOString(),
      technician: 'Alex Chen',
      status,
      notes
    };

    setRequests(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      return {
        ...req,
        dailyChecks: [check, ...(req.dailyChecks || [])],
        auditLog: [createAuditEntry('DAILY_CHECK', `Daily status confirmed: ${status}`), ...(req.auditLog || [])]
      };
    }));
  };

  const signAndApprove = (reqId: string) => {
    const signature = {
      approverName: 'Alex Chen',
      role: 'Lab Manager',
      timestamp: new Date().toISOString(),
      digitalHash: 'SHA256-' + Math.random().toString(36).substring(7)
    };

    setRequests(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      return {
        ...req,
        status: LabRequestStatus.CLOSED,
        approval: signature,
        auditLog: [createAuditEntry('SIGN_OFF', 'Final Approval Signature Applied'), ...(req.auditLog || [])]
      };
    }));
  };

  // --- Sub-Components ---

  const SchedulerView = () => {
    const [timelineStart, setTimelineStart] = useState(new Date('2025-02-01'));
    const dates = Array.from({ length: 30 }, (_, i) => {
      const d = new Date(timelineStart);
      d.setDate(d.getDate() + i);
      return d;
    });
    const getTestPosition = (req: LabTestRequest) => {
      if (!req.requestDate || !req.dueDate) return null;
      const start = new Date(req.requestDate);
      const end = new Date(req.dueDate);
      const tlStart = dates[0];
      const tlEnd = dates[dates.length - 1];
      const totalDays = 30;
      const dayMs = 86400000;
      const startDiff = start.getTime() - tlStart.getTime();
      const startOffsetDays = Math.floor(startDiff / dayMs);
      if (end < tlStart || start > tlEnd) return null;
      const visualStartDay = Math.max(0, startOffsetDays);
      const endOffsetDays = Math.floor((end.getTime() - tlStart.getTime()) / dayMs);
      const visualEndDay = Math.min(totalDays, endOffsetDays + 1);
      const visualDuration = visualEndDay - visualStartDay;
      if (visualDuration <= 0) return null;
      return {
        left: `${(visualStartDay / totalDays) * 100}%`,
        width: `${(visualDuration / totalDays) * 100}%`
      };
    };
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[calc(100vh-12rem)] overflow-hidden animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Calendar size={18} className="text-indigo-600" /> Rig Scheduler (Gantt)
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => { const d = new Date(timelineStart); d.setDate(d.getDate() - 7); setTimelineStart(d); }} className="p-1.5 hover:bg-slate-100 rounded border border-slate-200"><ChevronLeft size={16} /></button>
            <span className="text-sm font-medium text-slate-600 w-32 text-center">{timelineStart.toLocaleDateString()}</span>
            <button onClick={() => { const d = new Date(timelineStart); d.setDate(d.getDate() + 7); setTimelineStart(d); }} className="p-1.5 hover:bg-slate-100 rounded border border-slate-200"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto custom-scrollbar relative flex flex-col">
          <div className="flex border-b border-slate-200 sticky top-0 bg-slate-50 z-20">
            <div className="w-48 shrink-0 p-3 border-r border-slate-200 font-semibold text-xs text-slate-500 bg-slate-50 z-20 flex items-end">Equipment / Rig</div>
            <div className="flex-1 flex relative min-w-[900px]">
              {dates.map((d, i) => (
                <div key={i} className={`flex-1 border-r border-slate-100 p-2 text-center text-[10px] text-slate-500 min-w-[30px] ${d.getDay() === 0 || d.getDay() === 6 ? 'bg-slate-50' : 'bg-white'}`}>
                  <div className="font-bold">{d.getDate()}</div><div>{d.toLocaleDateString('en-US', { weekday: 'narrow' })}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[1148px]">
            {rigs.map(rig => {
              const assignedTests = requests.filter(r => r.assignedRigId === rig.id && r.status !== LabRequestStatus.CANCELLED && r.status !== LabRequestStatus.REJECTED);
              return (
                <div key={rig.id} className="flex border-b border-slate-100 hover:bg-slate-50/50 h-16 group relative">
                  <div className="w-48 shrink-0 p-3 border-r border-slate-200 bg-white group-hover:bg-slate-50 sticky left-0 z-10 flex flex-col justify-center shadow-[4px_0_10px_-4px_rgba(0,0,0,0.05)]">
                    <div className="font-medium text-sm text-slate-900 truncate" title={rig.name}>{rig.name}</div>
                    <div className="text-xs text-slate-500 truncate">{rig.type}</div>
                    {rig.status === RigStatus.MAINTENANCE && <span className="text-[10px] text-red-600 font-bold">MAINTENANCE</span>}
                  </div>
                  <div className="flex-1 relative bg-[linear-gradient(90deg,#f1f5f9_1px,transparent_1px)] bg-[size:3.333%_100%]">
                    {assignedTests.map(req => {
                      const style = getTestPosition(req);
                      if (!style) return null;
                      return (
                        <div key={req.id} className={`absolute top-2 bottom-2 rounded-md shadow-sm border border-white/20 text-[10px] px-2 flex flex-col justify-center text-white overflow-hidden cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md hover:z-20 ${req.status === LabRequestStatus.IN_PROGRESS ? 'bg-blue-500' : req.status === LabRequestStatus.COMPLETED ? 'bg-purple-500' : req.status === LabRequestStatus.APPROVED ? 'bg-emerald-500' : req.status === LabRequestStatus.CLOSED ? 'bg-slate-500' : 'bg-amber-500'}`} style={style} title={`${req.testName}\nStatus: ${req.status}\nProject: ${req.projectCode}\nDue: ${req.dueDate}`} onClick={() => { setSelectedRequest(req); setView('execution'); }}>
                          <div className="font-bold truncate">{req.testName}</div>
                          <div className="opacity-80 truncate">{req.projectCode}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="mt-6 border-t-2 border-slate-100">
              <div className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider bg-slate-50 sticky left-0">Unassigned Requests Queue</div>
              {requests.filter(r => !r.assignedRigId && r.status !== LabRequestStatus.CANCELLED && r.status !== LabRequestStatus.REJECTED && r.status !== LabRequestStatus.DRAFT).map(req => (
                <div key={req.id} className="flex border-b border-slate-100 h-10 hover:bg-amber-50 group">
                  <div className="w-48 shrink-0 p-2 border-r border-slate-200 flex items-center text-xs text-slate-500 italic bg-white sticky left-0 z-10 group-hover:bg-amber-50">Pending Assignment</div>
                  <div className="flex-1 relative p-2 flex items-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-amber-200 text-amber-800 rounded-full text-xs font-medium cursor-pointer hover:bg-amber-100 transition-colors shadow-sm" onClick={() => { setSelectedRequest(req); setView('execution'); }}>
                      <span className={`w-2 h-2 rounded-full ${req.priority === 'High' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                      {req.testName} ({req.priority}) - Due {req.dueDate}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ExecutionView = () => {
    const activeReq = requests.find(r => r.id === selectedRequest?.id);
    if (!activeReq) return <div className="flex flex-col items-center justify-center h-full text-slate-400"><AlertTriangle size={48} className="mb-4 text-slate-300" /><p>No request selected.</p><button onClick={() => setView('requests')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">Go to Requests</button></div>;
    const isCompleted = activeReq.status === LabRequestStatus.COMPLETED || activeReq.status === LabRequestStatus.CLOSED;
    return (
      <div className="flex gap-6 h-[calc(100vh-12rem)] animate-fade-in">
        <div className="w-1/3 flex flex-col gap-6 overflow-y-auto pr-2 custom-scrollbar">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-start mb-4">
              <div><h2 className="text-lg font-bold text-slate-900">{activeReq.testName}</h2><p className="text-sm text-slate-500">{activeReq.id}</p></div>
              <span className={`px-2 py-1 rounded text-xs font-bold ${activeReq.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{activeReq.priority}</span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">Project</span><span className="font-medium text-slate-700">{activeReq.projectCode}</span></div>
              <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">Rig</span><span className="font-medium text-slate-700">{activeReq.assignedRigId || 'Unassigned'}</span></div>
              <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">Samples</span><span className="font-medium text-slate-700">{activeReq.samples.join(', ')}</span></div>
            </div>
            <div className="mt-6"><h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><CheckSquare size={16} /> QC Gates</h4><div className="space-y-2">{Object.entries(activeReq.qcGates).map(([key, val]) => (<div key={key} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${val ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200 hover:border-indigo-300'}`} onClick={() => !isCompleted && handleQCGateToggle(activeReq.id, key as any)}><span className={`text-sm ${val ? 'text-emerald-700 font-medium' : 'text-slate-600'}`}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>{val ? <CheckCircle2 size={18} className="text-emerald-600" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300"></div>}</div>))}</div></div>
            <div className="mt-6 flex gap-2">
              {activeReq.status === LabRequestStatus.SUBMITTED && <button onClick={() => handleAutoAssign(activeReq.id)} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Assign Rig</button>}
              {activeReq.status === LabRequestStatus.IN_PROGRESS && <button onClick={() => handleStatusChange(activeReq.id, LabRequestStatus.COMPLETED)} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">Complete Test</button>}
              {activeReq.status === LabRequestStatus.COMPLETED && <button onClick={() => signAndApprove(activeReq.id)} className="flex-1 py-2 bg-indigo-900 text-white rounded-lg text-sm font-medium hover:bg-indigo-800">Sign & Approve</button>}
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex-1">
            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><History size={16} /> Audit Trail</h4>
            <div className="space-y-4 max-h-60 overflow-y-auto custom-scrollbar">{activeReq.auditLog?.map((log, idx) => (<div key={idx} className="relative pl-4 border-l-2 border-slate-100 last:border-0 pb-4 last:pb-0"><div className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full bg-slate-300 border-2 border-white"></div><p className="text-xs font-bold text-slate-700">{log.action}</p><p className="text-xs text-slate-500 mt-0.5">{log.details}</p><p className="text-[10px] text-slate-400 mt-1">{new Date(log.timestamp).toLocaleString()}</p></div>))}</div>
          </div>
        </div>
        <div className="w-2/3 flex flex-col gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-800 flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" /> Deviations / Issues</h3><button onClick={() => { const desc = prompt("Describe the deviation:"); if (desc) addDeviation(activeReq.id, 'Minor', desc); }} className="text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg font-medium hover:bg-amber-100">+ Log Issue</button></div>
            {(!activeReq.deviations || activeReq.deviations.length === 0) ? <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-500 text-center border border-slate-100 border-dashed">No deviations recorded.</div> : <div className="space-y-2">{activeReq.deviations.map(dev => (<div key={dev.id} className="p-3 bg-red-50 border border-red-100 rounded-lg flex justify-between items-start"><div><div className="flex items-center gap-2"><span className="font-bold text-red-800 text-sm">{dev.type}</span><span className="text-xs text-red-600 font-mono">{dev.id}</span></div><p className="text-sm text-red-900 mt-1">{dev.description}</p></div>{!dev.capaId ? <button onClick={() => handleEscalateCAPA(activeReq.id, dev.id)} className="text-xs bg-white border border-red-200 text-red-700 px-2 py-1 rounded shadow-sm hover:bg-red-50">Escalate to CAPA</button> : <span className="text-xs bg-white border border-emerald-200 text-emerald-700 px-2 py-1 rounded font-mono">{dev.capaId}</span>}</div>))}</div>}
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex-1 flex flex-col">
            <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-800 flex items-center gap-2"><CalendarCheck size={18} className="text-indigo-500" /> Daily Checks</h3><button onClick={() => { const notes = prompt("Enter check notes:"); if (notes) addDailyCheck(activeReq.id, 'Nominal', notes); }} className="text-xs bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg font-medium hover:bg-indigo-100">+ Add Check</button></div>
            <div className="flex-1 overflow-auto"><table className="w-full text-sm text-left"><thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="px-4 py-2">Time</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Technician</th><th className="px-4 py-2">Notes</th></tr></thead><tbody className="divide-y divide-slate-100">{activeReq.dailyChecks?.map(check => (<tr key={check.id}><td className="px-4 py-3 text-slate-600 text-xs">{new Date(check.timestamp).toLocaleDateString()}</td><td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${check.status === 'Nominal' ? 'bg-emerald-100 text-emerald-700' : check.status === 'Warning' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{check.status}</span></td><td className="px-4 py-3 text-slate-700">{check.technician}</td><td className="px-4 py-3 text-slate-500">{check.notes}</td></tr>))}{(!activeReq.dailyChecks || activeReq.dailyChecks.length === 0) && <tr><td colSpan={4} className="p-4 text-center text-slate-400 italic">No daily checks logged yet.</td></tr>}</tbody></table></div>
          </div>
        </div>
      </div>
    );
  };

  const RequestManager = () => {
    const [filter, setFilter] = useState('All');
    const filteredRequests = filter === 'All' ? requests : requests.filter(r => r.status === filter);
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-[calc(100vh-12rem)] flex flex-col animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div className="flex gap-2">{['All', 'Draft', 'Submitted', 'In Progress', 'Completed'].map(f => (<button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-xs font-medium rounded-full border ${filter === f ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>{f}</button>))}</div>
          <div className="relative"><input className="pl-8 pr-4 py-1.5 text-sm border border-slate-200 rounded-lg w-64 focus:outline-none focus:border-indigo-500" placeholder="Search requests..." /><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" /></div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm text-left"><thead className="bg-slate-50 text-slate-500 sticky top-0 z-10"><tr><th className="px-6 py-3">ID</th><th className="px-6 py-3">Test Name</th><th className="px-6 py-3">Project</th><th className="px-6 py-3">Status</th><th className="px-6 py-3">Due Date</th><th className="px-6 py-3">Assigned Rig</th><th className="px-6 py-3">Actions</th></tr></thead><tbody className="divide-y divide-slate-100">{filteredRequests.map(req => (<tr key={req.id} className="hover:bg-slate-50 group"><td className="px-6 py-4 font-mono text-xs text-slate-500">{req.id}</td><td className="px-6 py-4 font-medium text-slate-900">{req.testName}</td><td className="px-6 py-4 text-slate-600">{req.projectCode}</td><td className="px-6 py-4"><span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${req.status === LabRequestStatus.COMPLETED ? 'bg-emerald-100 text-emerald-700' : req.status === LabRequestStatus.IN_PROGRESS ? 'bg-blue-100 text-blue-700' : req.status === LabRequestStatus.SUBMITTED ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{req.status}</span></td><td className="px-6 py-4 text-slate-600">{req.dueDate}</td><td className="px-6 py-4 text-slate-600">{req.assignedRigId || '-'}</td><td className="px-6 py-4"><button onClick={() => { setSelectedRequest(req); setView('execution'); }} className="text-indigo-600 hover:text-indigo-900 font-medium text-xs">Open</button></td></tr>))}</tbody></table>
        </div>
      </div>
    );
  };

  const LimsDashboard = () => {
    const statusCounts = requests.reduce((acc, curr) => { acc[curr.status] = (acc[curr.status] || 0) + 1; return acc; }, {} as Record<string, number>);
    const data = [{ name: 'Draft', value: statusCounts[LabRequestStatus.DRAFT] || 0 }, { name: 'Submitted', value: statusCounts[LabRequestStatus.SUBMITTED] || 0 }, { name: 'In Progress', value: statusCounts[LabRequestStatus.IN_PROGRESS] || 0 }, { name: 'Completed', value: statusCounts[LabRequestStatus.COMPLETED] || 0 }];
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><p className="text-sm font-medium text-slate-500">Active Requests</p><h3 className="text-2xl font-bold text-slate-900 mt-2">{requests.filter(r => r.status !== 'Completed' && r.status !== 'Cancelled').length}</h3></div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><p className="text-sm font-medium text-slate-500">Rig Utilization</p><h3 className="text-2xl font-bold text-slate-900 mt-2">{Math.round((rigs.filter(r => r.status === RigStatus.IN_USE).length / rigs.length) * 100)}%</h3></div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><p className="text-sm font-medium text-slate-500">Pending Approval</p><h3 className="text-2xl font-bold text-slate-900 mt-2 text-amber-600">{statusCounts[LabRequestStatus.SUBMITTED] || 0}</h3></div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><p className="text-sm font-medium text-slate-500">Critical Deviations</p><h3 className="text-2xl font-bold text-slate-900 mt-2 text-red-600">{requests.reduce((acc, r) => acc + (r.deviations?.filter(d => d.type === 'Critical').length || 0), 0)}</h3></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><h3 className="font-semibold text-slate-800 mb-4">Request Status Distribution</h3><div className="h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={data}><XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} /><YAxis fontSize={12} tickLine={false} axisLine={false} /><Tooltip /><Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={40} /></BarChart></ResponsiveContainer></div></div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><h3 className="font-semibold text-slate-800 mb-4">Recent Audit Log</h3><div className="space-y-4 max-h-64 overflow-y-auto custom-scrollbar">{requests.flatMap(r => r.auditLog || []).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 10).map(log => (<div key={log.id} className="flex gap-3 text-sm"><div className="min-w-[4px] w-1 bg-slate-200 rounded-full"></div><div><p className="text-slate-900 font-medium">{log.action}</p><p className="text-slate-500 text-xs">{log.details} • {new Date(log.timestamp).toLocaleTimeString()}</p></div></div>))}</div></div>
        </div>
      </div>
    );
  };

  const AdminConsole = () => {
    const [adminView, setAdminView] = useState<'general' | 'users' | 'equipment' | 'methods'>('users');
    const [newUser, setNewUser] = useState<Partial<LabUser>>({});
    const [newRig, setNewRig] = useState<Partial<LabRig>>({});

    // Test Method Editor State
    const [editingMethod, setEditingMethod] = useState<any>(null);
    const [isEditingMethod, setIsEditingMethod] = useState(false);

    const handleSaveMethod = () => {
      if (!editingMethod.name) return;

      const newMethod = {
        ...editingMethod,
        id: editingMethod.id || `PROT-${Date.now().toString().slice(-3)}`,
      };

      if (editingMethod.id) {
        setProtocols(prev => prev.map(p => p.id === editingMethod.id ? newMethod : p));
      } else {
        setProtocols(prev => [...prev, newMethod]);
      }
      setIsEditingMethod(false);
      setEditingMethod(null);
    };

    const handleAddUser = () => {
      if (!newUser.name || !newUser.role) return;

      if (newUser.id) {
        // Update existing
        setUsers(users.map(u => u.id === newUser.id ? { ...u, ...newUser } as LabUser : u));
        setNewUser({});
      } else {
        // Create new
        const u: LabUser = {
          id: `U-${Date.now().toString().slice(-3)}`,
          name: newUser.name,
          email: newUser.email || `${newUser.name.toLowerCase().replace(' ', '.')}@reliability.pro`,
          role: newUser.role as LabRole,
          shift: newUser.shift as ShiftType || '1st Shift (Day)',
          status: newUser.status as any || 'Active',
          skills: []
        };
        setUsers([...users, u]);
        setNewUser({});
      }
    };

    const handleAddRig = () => {
      if (!newRig.name || !newRig.type) return;

      if (newRig.id) {
        // Update existing
        setRigs(rigs.map(r => r.id === newRig.id ? { ...r, ...newRig } as LabRig : r));
        setNewRig({});
      } else {
        // Create new
        const r: LabRig = {
          id: `RIG-${Date.now().toString().slice(-3)}`,
          name: newRig.name,
          type: newRig.type,
          status: RigStatus.AVAILABLE,
          calibrationDue: newRig.calibrationDue || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          location: newRig.location || 'Main Lab',
          capabilities: []
        };
        setRigs([...rigs, r]);
        setNewRig({});
      }
    };

    return (
      <div className="flex h-[calc(100vh-12rem)] bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
        {/* Admin Sidebar */}
        <div className="w-64 bg-slate-50 border-r border-slate-200 p-4 flex flex-col">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-2">Admin Console</h3>
          <div className="space-y-1">
            <button onClick={() => setAdminView('users')} className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${adminView === 'users' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Users size={18} /> Users & Shifts
            </button>
            <button onClick={() => setAdminView('equipment')} className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${adminView === 'equipment' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Wrench size={18} /> Equipment
            </button>
            <button onClick={() => setAdminView('methods')} className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${adminView === 'methods' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
              <BookOpen size={18} /> Test Methods
            </button>
            <button onClick={() => setAdminView('general')} className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${adminView === 'general' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Settings size={18} /> General Settings
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">

          {/* USERS VIEW */}
          {adminView === 'users' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">User Management</h2>
                  <p className="text-sm text-slate-500">Manage operators, shifts, and availability.</p>
                </div>
              </div>

              <div className="mb-6 p-4 border border-indigo-200 bg-indigo-50 rounded-lg animate-fade-in">
                <h4 className="font-bold text-indigo-900 mb-3 text-sm">{newUser.id ? 'Edit User' : 'Add New User'}</h4>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <input className="border rounded p-2 text-sm" placeholder="Full Name" value={newUser.name || ''} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
                  <select className="border rounded p-2 text-sm bg-white" value={newUser.role || ''} onChange={e => setNewUser({ ...newUser, role: e.target.value as LabRole })}>
                    <option value="">Role...</option>
                    <option value="Technician">Technician</option>
                    <option value="Manager">Manager</option>
                    <option value="Engineer">Engineer</option>
                  </select>
                  <select className="border rounded p-2 text-sm bg-white" value={newUser.shift || ''} onChange={e => setNewUser({ ...newUser, shift: e.target.value as ShiftType })}>
                    <option value="">Shift...</option>
                    <option value="1st Shift (Day)">1st Shift (Day)</option>
                    <option value="2nd Shift (Eve)">2nd Shift (Eve)</option>
                    <option value="Weekend">Weekend</option>
                  </select>
                  <button onClick={handleAddUser} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">
                    {newUser.id ? 'Update User' : 'Add User'}
                  </button>
                  {newUser.id && <button onClick={() => setNewUser({})} className="px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">Cancel</button>}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-6 py-3">Name</th>
                      <th className="px-6 py-3">Role</th>
                      <th className="px-6 py-3">Shift</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </td>
                        <td className="px-6 py-4"><span className="bg-slate-100 text-slate-600 px-2 py-1 rounded-full text-xs font-medium">{u.role}</span></td>
                        <td className="px-6 py-4 text-slate-600">{u.shift}</td>
                        <td className="px-6 py-4">
                          <span className={`flex items-center gap-1.5 text-xs font-medium ${u.status === 'Active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                            {u.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 flex gap-2">
                          <button onClick={() => setNewUser(u)} className="text-slate-400 hover:text-indigo-600"><Edit2 size={16} /></button>
                          <button onClick={() => setUsers(users.filter(user => user.id !== u.id))} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* EQUIPMENT VIEW */}
          {adminView === 'equipment' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Lab Equipment</h2>
                  <p className="text-sm text-slate-500">Manage assets, capabilities, and retirement.</p>
                </div>
              </div>

              <div className="mb-6 p-4 border border-indigo-200 bg-indigo-50 rounded-lg animate-fade-in">
                <h4 className="font-bold text-indigo-900 mb-3 text-sm">{newRig.id ? 'Edit Asset' : 'Add New Asset'}</h4>
                <div className="grid grid-cols-5 gap-4 mb-4">
                  <input className="border rounded p-2 text-sm" placeholder="Asset Name" value={newRig.name || ''} onChange={e => setNewRig({ ...newRig, name: e.target.value })} />
                  <input className="border rounded p-2 text-sm" placeholder="Type (e.g. Chamber)" value={newRig.type || ''} onChange={e => setNewRig({ ...newRig, type: e.target.value })} />
                  <input className="border rounded p-2 text-sm" placeholder="Location" value={newRig.location || ''} onChange={e => setNewRig({ ...newRig, location: e.target.value })} />
                  <input type="date" className="border rounded p-2 text-sm bg-white" placeholder="Cal Due Date" value={newRig.calibrationDue || ''} onChange={e => setNewRig({ ...newRig, calibrationDue: e.target.value })} />
                  <button onClick={handleAddRig} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">
                    {newRig.id ? 'Update Asset' : 'Add Asset'}
                  </button>
                  {newRig.id && <button onClick={() => setNewRig({})} className="px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">Cancel</button>}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-6 py-3">Asset ID</th>
                      <th className="px-6 py-3">Name</th>
                      <th className="px-6 py-3">Type</th>
                      <th className="px-6 py-3">Location</th>
                      <th className="px-6 py-3">Cal Due</th>
                      <th className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rigs.map(r => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-mono text-xs text-slate-500">{r.id}</td>
                        <td className="px-6 py-4 font-medium text-slate-900">{r.name}</td>
                        <td className="px-6 py-4 text-slate-600">{r.type}</td>
                        <td className="px-6 py-4 text-slate-600">{r.location}</td>
                        <td className="px-6 py-4 text-slate-600">{r.calibrationDue}</td>
                        <td className="px-6 py-4 flex gap-2">
                          <button onClick={() => setNewRig(r)} className="text-slate-400 hover:text-indigo-600"><Edit2 size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* METHODS VIEW (Updated from Protocols) */}
          {adminView === 'methods' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Test Method Templates</h2>
                  <p className="text-sm text-slate-500">Define standard procedures, parameters, and QC requirements.</p>
                </div>
                {!isEditingMethod && (
                  <button
                    onClick={() => {
                      setEditingMethod({ name: '', standard: '', duration: 24, gates: [], defaultParams: [] });
                      setIsEditingMethod(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                  >
                    <Plus size={16} /> Create Template
                  </button>
                )}
              </div>

              {isEditingMethod ? (
                <div className="bg-slate-50 border border-indigo-100 rounded-xl p-6 animate-fade-in shadow-sm">
                  <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    {editingMethod.id ? <Edit2 size={20} className="text-indigo-600" /> : <Plus size={20} className="text-indigo-600" />}
                    {editingMethod.id ? 'Edit Test Method' : 'New Test Method Template'}
                  </h3>

                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Method Name</label>
                      <input
                        className="w-full border border-slate-300 rounded-lg p-2.5 bg-white"
                        placeholder="e.g. Thermal Shock"
                        value={editingMethod.name}
                        onChange={e => setEditingMethod({ ...editingMethod, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Reference Standard</label>
                      <input
                        className="w-full border border-slate-300 rounded-lg p-2.5 bg-white"
                        placeholder="e.g. ISO 16750-4"
                        value={editingMethod.standard}
                        onChange={e => setEditingMethod({ ...editingMethod, standard: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Expected Duration (Hours)</label>
                      <input
                        type="number"
                        className="w-full border border-slate-300 rounded-lg p-2.5 bg-white"
                        value={editingMethod.duration}
                        onChange={e => setEditingMethod({ ...editingMethod, duration: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Required QC Gates</label>
                    <div className="flex gap-4">
                      {['calibrationChecked', 'samplesVerified', 'environmentChecked'].map(gate => (
                        <label key={gate} className="flex items-center gap-2 bg-white px-3 py-2 border rounded-lg cursor-pointer hover:border-indigo-300">
                          <input
                            type="checkbox"
                            checked={editingMethod.gates.includes(gate)}
                            onChange={e => {
                              if (e.target.checked) setEditingMethod({ ...editingMethod, gates: [...editingMethod.gates, gate] });
                              else setEditingMethod({ ...editingMethod, gates: editingMethod.gates.filter((g: string) => g !== gate) });
                            }}
                            className="rounded text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-slate-600">
                            {gate.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-bold text-slate-700">Test Parameters</label>
                      <button
                        onClick={() => setEditingMethod({
                          ...editingMethod,
                          defaultParams: [...(editingMethod.defaultParams || []), { key: '', label: '', type: 'text', default: '', unit: '' }]
                        })}
                        className="text-xs text-indigo-600 font-medium hover:underline"
                      >
                        + Add Parameter
                      </button>
                    </div>
                    <div className="space-y-2">
                      {editingMethod.defaultParams?.map((param: any, idx: number) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input placeholder="Key (e.g. temp)" className="border rounded p-2 text-xs w-24" value={param.key} onChange={e => {
                            const newParams = [...editingMethod.defaultParams];
                            newParams[idx].key = e.target.value;
                            setEditingMethod({ ...editingMethod, defaultParams: newParams });
                          }} />
                          <input placeholder="Label" className="border rounded p-2 text-xs flex-1" value={param.label} onChange={e => {
                            const newParams = [...editingMethod.defaultParams];
                            newParams[idx].label = e.target.value;
                            setEditingMethod({ ...editingMethod, defaultParams: newParams });
                          }} />
                          <select className="border rounded p-2 text-xs w-24" value={param.type} onChange={e => {
                            const newParams = [...editingMethod.defaultParams];
                            newParams[idx].type = e.target.value;
                            setEditingMethod({ ...editingMethod, defaultParams: newParams });
                          }}>
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                          </select>
                          <input placeholder="Default" className="border rounded p-2 text-xs w-24" value={param.default} onChange={e => {
                            const newParams = [...editingMethod.defaultParams];
                            newParams[idx].default = e.target.value;
                            setEditingMethod({ ...editingMethod, defaultParams: newParams });
                          }} />
                          <input placeholder="Unit" className="border rounded p-2 text-xs w-16" value={param.unit} onChange={e => {
                            const newParams = [...editingMethod.defaultParams];
                            newParams[idx].unit = e.target.value;
                            setEditingMethod({ ...editingMethod, defaultParams: newParams });
                          }} />
                          <button onClick={() => {
                            const newParams = editingMethod.defaultParams.filter((_: any, i: number) => i !== idx);
                            setEditingMethod({ ...editingMethod, defaultParams: newParams });
                          }} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                        </div>
                      ))}
                      {(!editingMethod.defaultParams || editingMethod.defaultParams.length === 0) && (
                        <p className="text-xs text-slate-400 italic">No parameters defined.</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                    <button onClick={() => setIsEditingMethod(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-white">Cancel</button>
                    <button onClick={handleSaveMethod} className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 shadow-sm">Save Template</button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {protocols.map(prot => (
                    <div
                      key={prot.id}
                      className="p-5 border border-slate-200 rounded-xl hover:border-indigo-300 transition-all bg-white group shadow-sm flex flex-col"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">{prot.name}</h4>
                          <p className="text-xs text-slate-500 font-mono mt-1 bg-slate-100 inline-block px-1.5 py-0.5 rounded">{prot.standard}</p>
                        </div>
                        <button
                          onClick={() => { setEditingMethod(prot); setIsEditingMethod(true); }}
                          className="text-slate-400 hover:text-indigo-600 p-1 rounded hover:bg-indigo-50"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>

                      <div className="mt-auto pt-3 border-t border-slate-50 flex justify-between items-center text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Clock3 size={14} /> {prot.duration} hrs</span>
                        <span className="flex items-center gap-1"><List size={14} /> {prot.defaultParams?.length || 0} Params</span>
                        <span className="flex items-center gap-1"><ShieldAlert size={14} /> {prot.gates.length} Gates</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* GENERAL SETTINGS VIEW */}
          {adminView === 'general' && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-6">General System Settings</h2>
              <div className="space-y-4 max-w-2xl">
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900">Auto-Lock Rigs on Expiry</h4>
                    <p className="text-xs text-slate-500">Prevent assignment if calibration is overdue.</p>
                  </div>
                  <ToggleRight size={32} className="text-emerald-500 cursor-pointer" />
                </div>
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900">Require E-Signature for Critical Deviations</h4>
                    <p className="text-xs text-slate-500">Force manager approval step.</p>
                  </div>
                  <ToggleRight size={32} className="text-emerald-500 cursor-pointer" />
                </div>
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900">Daily Digest Email</h4>
                    <p className="text-xs text-slate-500">Send summary report to admins at 08:00.</p>
                  </div>
                  <ToggleLeft size={32} className="text-slate-300 cursor-pointer" />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  };

  const SampleManager = () => {
    const handlePrintLabels = () => {
      // Simulate print
      window.print();
    };

    const handleCheckInSample = () => {
      const id = `S-${Date.now().toString().slice(-4)}`;
      const newSample: LabSample = {
        id,
        name: 'New Sample',
        batch: 'B-' + new Date().getFullYear(),
        status: 'Received',
        location: 'Receiving Dock'
      };
      setSamples([newSample, ...samples]);
      // In a real app, this would open a modal
      alert(`Sample ${id} checked in successfully!`);
    };

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-[calc(100vh-12rem)] flex flex-col animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <QrCode size={18} className="text-slate-500" />
            Sample Inventory
          </h3>
          <div className="flex gap-2">
            <button onClick={handlePrintLabels} className="text-xs bg-white border border-slate-300 px-3 py-1.5 rounded hover:bg-slate-50">Print Labels</button>
            <button onClick={handleCheckInSample} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700">+ Check-in Sample</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="px-6 py-3">ID / Barcode</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Batch</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Location</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {samples.map(sample => (
                <tr key={sample.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-mono text-xs text-slate-600 font-bold">{sample.id}</td>
                  <td className="px-6 py-4 font-medium text-slate-900">{sample.name}</td>
                  <td className="px-6 py-4 text-slate-600">{sample.batch}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${sample.status === 'In Testing' ? 'bg-blue-100 text-blue-700' :
                      sample.status === 'Received' ? 'bg-emerald-100 text-emerald-700' :
                        sample.status === 'Stored' ? 'bg-slate-100 text-slate-600' :
                          'bg-red-100 text-red-700'
                      }`}>
                      {sample.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{sample.location}</td>
                  <td className="px-6 py-4 flex gap-2">
                    <button
                      onClick={() => handleSampleAction(sample.id, 'Stored')}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Move to Storage
                    </button>
                    <button
                      onClick={() => handleSampleAction(sample.id, 'Disposed')}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Dispose
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const RigManager = () => {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-[calc(100vh-12rem)] flex flex-col animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Wrench size={18} className="text-slate-500" />
            Equipment Management
          </h3>
          <div className="flex gap-2">
            {/* Action buttons could go here */}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="px-6 py-3">ID</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Cal Due</th>
                <th className="px-6 py-3">Location</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rigs.map(rig => (
                <tr key={rig.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-mono text-xs text-slate-600 font-bold">{rig.id}</td>
                  <td className="px-6 py-4 font-medium text-slate-900">{rig.name}</td>
                  <td className="px-6 py-4 text-slate-600">{rig.type}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${rig.status === RigStatus.AVAILABLE ? 'bg-emerald-100 text-emerald-700' :
                      rig.status === RigStatus.IN_USE ? 'bg-blue-100 text-blue-700' :
                        rig.status === RigStatus.MAINTENANCE ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                      }`}>
                      {rig.status}
                    </span>
                  </td>
                  <td className={`px-6 py-4 text-slate-600 ${new Date(rig.calibrationDue) < new Date() ? 'text-red-600 font-bold' : ''}`}>
                    {rig.calibrationDue}
                  </td>
                  <td className="px-6 py-4 text-slate-600">{rig.location}</td>
                  <td className="px-6 py-4 flex gap-2">
                    <button
                      onClick={() => handleRigAction(rig.id, 'maintenance')}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      {rig.status === RigStatus.MAINTENANCE ? 'End Maint.' : 'Maintenance'}
                    </button>
                    <button
                      onClick={() => handleRigAction(rig.id, 'calibrate')}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Calibrate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const NewRequestModal = () => {
    const [formData, setFormData] = useState({
      projectCode: MOCK_PROJECTS[0].code,
      testName: '',
      protocolId: '',
      sampleCount: 1,
      dueDate: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const project = MOCK_PROJECTS.find(p => p.code === formData.projectCode);
      const protocol = protocols.find(p => p.id === formData.protocolId);

      const newReq: LabTestRequest = {
        id: `LR-2025-${(requests.length + 1).toString().padStart(3, '0')}`,
        projectCode: formData.projectCode,
        customer: project?.customer,
        testName: formData.testName || protocol?.name || 'Untitled Test',
        requestor: 'Alex Chen',
        priority: 'Normal',
        status: LabRequestStatus.SUBMITTED,
        requestDate: new Date().toISOString().split('T')[0],
        dueDate: formData.dueDate,
        samples: Array.from({ length: formData.sampleCount }, (_, i) => `S-${Date.now().toString().slice(-4)}-${i + 1}`),
        qcGates: { calibrationChecked: false, samplesVerified: false, environmentChecked: false },
        protocolId: formData.protocolId,
        auditLog: [createAuditEntry('CREATE', 'Test request created via Wizard')]
      };

      setRequests(prev => [newReq, ...prev]);

      // Also generate samples
      const newSamples: LabSample[] = newReq.samples.map(sid => ({
        id: sid,
        name: 'New Sample',
        batch: 'B-' + new Date().toISOString().split('T')[0],
        status: 'Created',
        location: 'Receiving'
      }));
      setSamples(prev => [...prev, ...newSamples]);

      setIsNewRequestOpen(false);
    };

    return (
      <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg animate-fade-in-up">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-900">Create Test Request</h3>
            <button onClick={() => setIsNewRequestOpen(false)} className="text-slate-400 hover:text-slate-600"><XCircle size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Project</label>
              <select
                className="w-full border border-slate-300 rounded-lg p-2 text-sm bg-white"
                value={formData.projectCode}
                onChange={e => setFormData({ ...formData, projectCode: e.target.value })}
              >
                {MOCK_PROJECTS.map(p => <option key={p.code} value={p.code}>{p.name} ({p.customer})</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Test Protocol</label>
              <select
                className="w-full border border-slate-300 rounded-lg p-2 text-sm bg-white"
                value={formData.protocolId}
                onChange={e => setFormData({ ...formData, protocolId: e.target.value, testName: protocols.find(p => p.id === e.target.value)?.name || '' })}
              >
                <option value="">Select Standard Protocol...</option>
                {protocols.map(p => <option key={p.id} value={p.id}>{p.name} ({p.standard})</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Test Name (Custom)</label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                value={formData.testName}
                onChange={e => setFormData({ ...formData, testName: e.target.value })}
                placeholder="e.g. Thermal Cycle Profile B"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sample Qty</label>
                <input
                  type="number"
                  min="1"
                  className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                  value={formData.sampleCount}
                  onChange={e => setFormData({ ...formData, sampleCount: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                <input
                  type="date"
                  className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                  value={formData.dueDate}
                  onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="pt-4 flex gap-3">
              <button type="button" onClick={() => setIsNewRequestOpen(false)} className="flex-1 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200">Cancel</button>
              <button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700">Submit Request</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full space-y-6 relative">
      {/* Header & View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Lab Management (LIMS)</h2>
          <p className="text-sm text-slate-500">Orchestrate workflow, assets, and compliance.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsNewRequestOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 shadow-sm transition-colors"
          >
            <Plus size={16} /> New Request
          </button>
          <div className="flex p-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard' },
              { id: 'scheduler', label: 'Scheduler' },
              { id: 'requests', label: 'Requests' },
              { id: 'execution', label: 'Execution' },
              { id: 'samples', label: 'Samples' },
              { id: 'rigs', label: 'Equipment' },
              { id: 'admin', label: 'Admin' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id as any)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap ${view === tab.id
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0">
        {view === 'dashboard' && <LimsDashboard />}
        {view === 'scheduler' && <SchedulerView />}
        {view === 'requests' && <RequestManager />}
        {view === 'execution' && <ExecutionView />}
        {view === 'samples' && <SampleManager />}
        {view === 'rigs' && <RigManager />}
        {view === 'admin' && <AdminConsole />}
      </div>

      {/* New Request Modal */}
      {isNewRequestOpen && <NewRequestModal />}
    </div>
  );
};
